import { Button, Flex, IconButton, Select, Text } from "@chakra-ui/react";
import {
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

interface CreditAuctionProposalsPaginationProps {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  /** Quando o backend não expõe a contagem total de itens, omita — o texto "Total de X propostas" some. */
  totalItems?: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  /** Margem vertical (Chakra spacing). Permite tratar topo/rodapé diferente. */
  mt?: number;
  mb?: number;
}

const SIBLINGS = 1;
const BOUNDARIES = 1;

function getVisiblePages(
  currentPage: number,
  totalPages: number
): (number | "ellipsis-left" | "ellipsis-right")[] {
  if (totalPages <= 1) return [1];

  const startPages = Array.from(
    { length: Math.min(BOUNDARIES, totalPages) },
    (_, i) => i + 1
  );
  const endPages = Array.from(
    { length: Math.min(BOUNDARIES, totalPages) },
    (_, i) => totalPages - i
  ).reverse();

  const siblingsStart = Math.max(currentPage - SIBLINGS, BOUNDARIES + 1);
  const siblingsEnd = Math.min(currentPage + SIBLINGS, totalPages - BOUNDARIES);

  const middle: number[] = [];
  for (let p = siblingsStart; p <= siblingsEnd; p++) middle.push(p);

  const result: (number | "ellipsis-left" | "ellipsis-right")[] = [...startPages];
  if (siblingsStart > BOUNDARIES + 1) result.push("ellipsis-left");
  result.push(...middle);
  if (siblingsEnd < totalPages - BOUNDARIES) result.push("ellipsis-right");
  for (const page of endPages) {
    if (!result.includes(page)) result.push(page);
  }

  return result;
}

export default function CreditAuctionProposalsPagination({
  currentPage,
  totalPages,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  mt = 4,
  mb = 4,
}: CreditAuctionProposalsPaginationProps) {
  if (!totalPages) {
    return null;
  }

  const isFirst = currentPage <= 1;
  const isLast = currentPage >= totalPages;
  const visiblePages = getVisiblePages(currentPage, totalPages);

  return (
    <Flex
      justifyContent="center"
      alignItems="center"
      mt={mt}
      mb={mb}
      gap={2}
      flexWrap="wrap"
      fontFamily="Lexend Deca"
    >
      <IconButton
        icon={<FaAngleDoubleLeft />}
        onClick={() => onPageChange(1)}
        isDisabled={isFirst}
        aria-label="Primeira página"
        size="sm"
        variant="ghost"
        colorScheme="teal"
      />
      <IconButton
        icon={<FaChevronLeft />}
        onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
        isDisabled={isFirst}
        aria-label="Página anterior"
        size="sm"
        variant="ghost"
        colorScheme="teal"
      />

      <Flex gap={1}>
        {visiblePages.map((page) =>
          page === "ellipsis-left" || page === "ellipsis-right" ? (
            <Text
              key={page}
              minW="32px"
              h="32px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              color="gray.500"
              fontSize="sm"
            >
              …
            </Text>
          ) : (
            <Button
              key={page}
              size="sm"
              minW="32px"
              h="32px"
              px={2}
              variant={page === currentPage ? "solid" : "ghost"}
              colorScheme="teal"
              onClick={() => onPageChange(page)}
              fontFamily="Lexend Deca"
              fontWeight={page === currentPage ? "600" : "normal"}
            >
              {page}
            </Button>
          )
        )}
      </Flex>

      <IconButton
        icon={<FaChevronRight />}
        onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
        isDisabled={isLast}
        aria-label="Próxima página"
        size="sm"
        variant="ghost"
        colorScheme="teal"
      />
      <IconButton
        icon={<FaAngleDoubleRight />}
        onClick={() => onPageChange(totalPages)}
        isDisabled={isLast}
        aria-label="Última página"
        size="sm"
        variant="ghost"
        colorScheme="teal"
      />

      <Flex alignItems="center" gap={2} ml={3}>
        <Text fontSize="sm" color="gray.600">
          por página
        </Text>
        <Select
          value={pageSize}
          onChange={(event) => onPageSizeChange(Number(event.target.value))}
          size="sm"
          width="auto"
          borderRadius="md"
          fontFamily="Lexend Deca"
        >
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </Select>
      </Flex>

      {totalItems !== undefined && (
        <Text fontSize="sm" color="gray.600" ml={3}>
          Total de {totalItems.toLocaleString("pt-BR")}{" "}
          {totalItems === 1 ? "proposta" : "propostas"}
        </Text>
      )}
    </Flex>
  );
}
